import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, Ban, Calendar, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/**
 * Razones predefinidas para un ciclo abortado. Se mantienen locales
 * a este módulo (también viven en el modal de creación) para que
 * este componente sea autónomo y no dependa de otros archivos del
 * módulo reception.
 */
const ABORT_REASONS = [
  'Mantenimiento',
  'Problemas eléctricos',
  'Error Máquina',
  'Programa mal seleccionado',
  'Otro',
];

/**
 * Convierte un ISO date a "YYYY-MM-DD" usando métodos LOCALES
 * (no UTC) para que la fecha mostrada en el input corresponda
 * a la fecha OPERATIVA que el operador registró (sin el desfase
 * de un día que se produce cerca del cambio de jornada).
 */
const isoToLocalDateInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Convierte "YYYY-MM-DD" del input a ISO con hora 12:00 UTC
 * (mismo criterio que el modal de creación, para que el backend
 * no aplique el "-1 día" accidentalmente al re-editar).
 */
const localDateInputToISO = (yyyyMmDd) => {
  if (!yyyyMmDd) return null;
  const [year, month, day] = yyyyMmDd.split('-').map((s) => parseInt(s, 10));
  if (!year || !month || !day) return null;
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).toISOString();
};

/**
 * Modal de edición dedicado a registros de ciclos de lavado
 * ABORTADOS.
 *
 * Es un componente aparte (no reutiliza EditWashingCycleModal)
 * porque la edición de un abortado tiene semánticas distintas:
 *   - NO se editan contadores (no aplican: un abortado no
 *     incrementa el contador de la máquina).
 *   - SÍ se edita la razón (puede corregirse o ampliarse) y la
 *     fecha (por si se registró en el día equivocado).
 *   - Los kilos lavados quedan forzados a 0 (un abortado no
 *     produjo kilos lavados).
 *
 * Props:
 *   - isOpen:    boolean
 *   - onClose:   () => void
 *   - onSave:    async (data) => void
 *   - item:      registro de WashingCycle a editar (con _id, fecha,
 *                maquina, ciclos, razonAbortado, etc.)
 *   - machines:  array de lavadoras disponibles
 *   - isLoading: boolean (estado de guardado del padre)
 */
const EditAbortedWashingCycleModal = ({
  isOpen,
  onClose,
  onSave,
  item,
  machines = [],
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({
    fecha: '',
    maquinaId: '',
    maquinaNombre: '',
    ciclos: '',
    razon: '',
    otraRazon: '',
  });
  const [errors, setErrors] = useState({});

  // Sincroniza el formulario cuando cambia el item o se abre el modal.
  useEffect(() => {
    if (isOpen && item) {
      // Si la razón guardada NO está dentro de las predefinidas,
      // la tratamos como "Otro" para que el operador pueda editarla
      // en el campo libre sin perder lo que escribió originalmente.
      const razonGuardada = item.razonAbortado || '';
      const esRazonConocida = ABORT_REASONS.includes(razonGuardada);
      setFormData({
        fecha: isoToLocalDateInput(item.fecha),
        maquinaId: item.maquina?.id || '',
        maquinaNombre: item.maquina?.nombre || '',
        ciclos: item.ciclos != null ? String(item.ciclos) : '',
        razon: esRazonConocida ? razonGuardada : (razonGuardada ? 'Otro' : ''),
        otraRazon: esRazonConocida ? '' : razonGuardada,
      });
      setErrors({});
    }
  }, [isOpen, item]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleMachineChange = useCallback(
    (machineId) => {
      const selected = machines.find((m) => m._id === machineId);
      setFormData((prev) => ({
        ...prev,
        maquinaId,
        maquinaNombre: selected?.nombre || '',
      }));
      if (errors.maquinaId) {
        setErrors((prev) => ({ ...prev, maquinaId: null }));
      }
    },
    [machines, errors],
  );

  const handleRazonChange = (value) => {
    // Si cambia la razón, limpiamos "otraRazon" salvo que la nueva
    // razón sea "Otro" — así evitamos arrastrar texto residual de
    // una razón anterior.
    setFormData((prev) => ({
      ...prev,
      razon: value,
      otraRazon: value === 'Otro' ? prev.otraRazon : '',
    }));
    if (errors.razon) {
      setErrors((prev) => ({ ...prev, razon: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.fecha) {
      newErrors.fecha = 'La fecha es requerida';
    }
    if (!formData.maquinaId) {
      newErrors.maquinaId = 'La lavadora es requerida';
    }
    const ciclosNum = parseInt(formData.ciclos, 10);
    if (!formData.ciclos || Number.isNaN(ciclosNum) || ciclosNum <= 0) {
      newErrors.ciclos = 'La cantidad de ciclos debe ser mayor a 0';
    }
    if (!formData.razon) {
      newErrors.razon = 'Selecciona la razón del abortado';
    } else if (formData.razon === 'Otro' && !formData.otraRazon?.trim()) {
      newErrors.otraRazon = 'Especifica la razón';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!validate() || !item?._id) return;

    const razonFinal =
      formData.razon === 'Otro' ? formData.otraRazon.trim() : formData.razon;

    const fechaISO = localDateInputToISO(formData.fecha);

    const dataToSave = {
      fecha: fechaISO,
      maquina: {
        id: formData.maquinaId,
        nombre: formData.maquinaNombre,
      },
      ciclos: parseInt(formData.ciclos, 10),
      razonAbortado: razonFinal,
      // Se preservan los campos que el operador NO edita desde este
      // modal pero que el documento debe mantener consistentes:
      abortado: true,
      kilosLavados: 0,
      lavanderia: item.lavanderia,
      capacidad: item.capacidad,
    };

    onSave(dataToSave);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/50"
          onClick={isLoading ? undefined : onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative z-10 w-full max-w-lg mx-4"
        >
          <Card className="shadow-2xl border-amber-300/70">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b bg-amber-50/50 dark:bg-amber-950/10 rounded-t-lg">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2 text-amber-700 dark:text-amber-400">
                  <Ban className="h-5 w-5" />
                  Editar ciclo abortado
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Corrige la fecha, la lavadora, la cantidad de ciclos o la razón.
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                disabled={isLoading}
                aria-label="Cerrar"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            <form onSubmit={handleSubmit}>
              <CardContent className="p-6 space-y-4">
                {/* Advertencia de campos no editables */}
                <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50/60 border border-amber-200/70 text-amber-800 dark:bg-amber-950/20 dark:border-amber-800/60 dark:text-amber-300 text-xs">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <p>
                    Los <strong>contadores</strong> y los <strong>kilos lavados</strong> no
                    se editan aquí (un ciclo abortado no incrementa el contador de la
                    máquina y siempre tiene 0 kg).
                  </p>
                </div>

                {/* Fecha */}
                <div className="space-y-1.5">
                  <Label htmlFor="aborted-edit-fecha" className="text-sm font-medium">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" /> Fecha del ciclo
                    </span>
                  </Label>
                  <Input
                    id="aborted-edit-fecha"
                    type="date"
                    value={formData.fecha}
                    onChange={(e) => handleChange('fecha', e.target.value)}
                    className={errors.fecha ? 'border-red-500' : ''}
                  />
                  {errors.fecha && (
                    <p className="text-xs text-red-500">{errors.fecha}</p>
                  )}
                </div>

                {/* Máquina */}
                <div className="space-y-1.5">
                  <Label htmlFor="aborted-edit-machine" className="text-sm font-medium">
                    Lavadora
                  </Label>
                  <Select
                    value={formData.maquinaId}
                    onValueChange={handleMachineChange}
                  >
                    <SelectTrigger
                      id="aborted-edit-machine"
                      className={errors.maquinaId ? 'border-red-500' : ''}
                    >
                      <SelectValue placeholder="Seleccionar lavadora..." />
                    </SelectTrigger>
                    <SelectContent>
                      {machines.map((m) => (
                        <SelectItem key={m._id} value={m._id}>
                          {m.nombre}
                          {m.capacidad ? ` · ${m.capacidad} kg` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.maquinaId && (
                    <p className="text-xs text-red-500">{errors.maquinaId}</p>
                  )}
                </div>

                {/* Cantidad de ciclos */}
                <div className="space-y-1.5">
                  <Label htmlFor="aborted-edit-ciclos" className="text-sm font-medium">
                    Cantidad de ciclos
                  </Label>
                  <Input
                    id="aborted-edit-ciclos"
                    type="number"
                    min="1"
                    value={formData.ciclos}
                    onChange={(e) => handleChange('ciclos', e.target.value)}
                    placeholder="0"
                    className={errors.ciclos ? 'border-red-500' : ''}
                  />
                  {errors.ciclos && (
                    <p className="text-xs text-red-500">{errors.ciclos}</p>
                  )}
                </div>

                {/* Razón */}
                <div className="space-y-1.5">
                  <Label htmlFor="aborted-edit-razon" className="text-sm font-medium">
                    Razón del abortado
                  </Label>
                  <Select
                    value={formData.razon}
                    onValueChange={handleRazonChange}
                  >
                    <SelectTrigger
                      id="aborted-edit-razon"
                      className={errors.razon ? 'border-red-500' : ''}
                    >
                      <SelectValue placeholder="Seleccionar razón..." />
                    </SelectTrigger>
                    <SelectContent>
                      {ABORT_REASONS.map((r) => (
                        <SelectItem key={r} value={r}>
                          {r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.razon && (
                    <p className="text-xs text-red-500">{errors.razon}</p>
                  )}
                </div>

                {/* Especificar razón (cuando es "Otro") */}
                <AnimatePresence>
                  {formData.razon === 'Otro' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.18 }}
                      className="space-y-1.5 overflow-hidden"
                    >
                      <Label htmlFor="aborted-edit-otra" className="text-sm font-medium">
                        Especificar razón
                      </Label>
                      <Input
                        id="aborted-edit-otra"
                        placeholder="Describe la razón..."
                        value={formData.otraRazon}
                        onChange={(e) => handleChange('otraRazon', e.target.value)}
                        className={errors.otraRazon ? 'border-red-500' : ''}
                      />
                      {errors.otraRazon && (
                        <p className="text-xs text-red-500">{errors.otraRazon}</p>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </CardContent>

              {/* Footer */}
              <div className="flex justify-end gap-3 p-6 border-t bg-muted/50">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isLoading}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {isLoading ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Guardar cambios
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Card>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default EditAbortedWashingCycleModal;
